import { Form, Formik } from "formik";
import { useState, type ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { object, string } from "yup";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import PasswordInput from "../UI/PasswordInput";
import styles from "./AuthForm.module.css";
// @ts-expect-error pure js
import axios from "../../api";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth.js";
import Callout from "../UI/Callout";
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_REGEX,
} from "../../utils/constants/validation";

interface LoginValues {
  email: string;
  password: string;
  persist_login: boolean;
  totp_code: string;
}

interface AuthFormProps {
  onPersistLoginChange?: (persist: boolean) => void;
}

export default function AuthForm({ onPersistLoginChange }: AuthFormProps) {
  const { buildAndSetAuth } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state?.from?.pathname ?? "/") +
    (location.state?.from?.search ?? "");

  const [errMsg, setErrMsg] = useState("");
  const [requiresTotp, setRequiresTotp] = useState(false);

  async function logMeIn(values: LoginValues) {
    setErrMsg("");
    try {
      const data = { ...values };
      const response = await axios.post("/token", data);
      buildAndSetAuth(response.data);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const error = err as {
        response?: {
          status: number;
          data?: { requires_totp?: boolean; error?: string };
        };
      };
      if (!error?.response) {
        setErrMsg("Serveur de connexion injoignable");
        console.error(error);
      } else if (error.response?.status === 401) {
        const errorData = error.response.data;
        if (errorData?.requires_totp) {
          setRequiresTotp(true);
        } else {
          setErrMsg(
            errorData?.error || "Adresse e-mail ou mot de passe incorrect",
          );
        }
      } else {
        setErrMsg("Erreur lors de la connexion");
        console.error(error);
      }
    }
  }

  const initialValues: LoginValues = {
    email: "",
    password: "",
    persist_login: false,
    totp_code: "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={object({
        email: string()
          .matches(EMAIL_REGEX, EMAIL_INVALID_MESSAGE)
          .required("Adresse e-mail requise "),
        totp_code: requiresTotp
          ? string()
              .required("Code TOTP requis")
              .matches(/^[\dA-F]{6,8}$/i, "Code TOTP invalide")
          : string().optional(),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={logMeIn}
    >
      {({ setFieldValue }) => {
        return (
          <Form className={styles.form}>
            <TextInput
              type="email"
              name="email"
              label="Adresse e-mail"
              placeholder="votre@email.fr"
              marginForm=""
            />
            <PasswordInput
              name="password"
              label="Mot de passe"
              placeholder="Votre mot de passe"
              marginForm=""
            />
            {requiresTotp && (
              <TextInput
                name="totp_code"
                placeholder="123456"
                autoFocus
                marginForm=""
              />
            )}
            <Callout variant="danger">{errMsg}</Callout>
            <FormikSubmitButton className={styles.submitButton}>
              Se connecter
            </FormikSubmitButton>
            <div className={styles.rememberMe}>
              <Checkbox
                name="persist_login"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("persist_login", e.target.checked);
                  onPersistLoginChange?.(e.target.checked);
                }}
              >
                Se souvenir de moi
              </Checkbox>
              <Link to="../reset-password" replace>
                Mot de passe oublié ?
              </Link>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
